'use client';

import clsx from 'clsx';

import type { ChangeEvent, ReactElement } from 'react';
import { cloneElement, useEffect, useRef, useState } from 'react';

import type { CarbonIconProps } from '@carbon/icons-react';
import { Close } from '@carbon/icons-react';

import styles from './text-input.module.scss';

export interface Props {
  customIcon?: ReactElement<CarbonIconProps>;
  placeholder?: string;
  onKeyUp?: (value: string) => void;
  debounceKeyUp?: number;
  className?: string;
}

export function TextInput({
  customIcon,
  placeholder,
  debounceKeyUp = 0,
  onKeyUp,
  className,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  function triggerDebouncedCallback(value: string) {
    if (!onKeyUp) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onKeyUp(value);
    }, debounceKeyUp);
  }

  function handleKeyUp() {
    if (!inputRef.current) return;
    const value = inputRef.current.value;
    setHasContent(value.length > 0);
    triggerDebouncedCallback(value);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setHasContent(value.length > 0);
    triggerDebouncedCallback(value);
  }

  function resetInput() {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.value = '';
    setHasContent(false);

    if (onKeyUp) {
      onKeyUp('');
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }

  const renderIcon =
    customIcon &&
    cloneElement(customIcon, {
      ...customIcon.props,
      width: 16,
      height: 16,
    });

  return (
    <div className={styles['text-input__wrapper']}>
      <input
        className={clsx(
          styles['text-input'],
          {
            [styles['text-input--with-icon']]: !!customIcon,
          },
          className
        )}
        type="text"
        placeholder={placeholder}
        onKeyUp={handleKeyUp}
        onChange={handleChange}
        ref={inputRef}
      />
      {renderIcon && <div className={styles['text-input__custom-icon']}>{renderIcon}</div>}
      {hasContent && (
        <button className={styles['text-input__reset-button']} onClick={() => resetInput()}>
          <Close width={16} height={16} />
        </button>
      )}
    </div>
  );
}
